import React, { useState, useLayoutEffect, useEffect } from "react";
import { getApiUrl } from "../config/api";
import RFQStream from "./RFQStream";
import { useSettings } from "../context/SettingsContext";
import { findDealersNearby } from "../services/settingsService";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";
import { groupService } from "../services/groupService";

export default function RFQScreen({ 
  title, 
  unit = "units", 
  apiEndpoint, 
  dealers = [],
  placeholder = "Enter quantity...",
  clientId,
  materialCategory,
  materialSubcategory
}) {
  const { settings, updateMaterialRadiusSetting, getMaterialRadiusSetting } = useSettings();
  const [quantity, setQuantity] = useState("");
  const [selectedDealers, setSelectedDealers] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [activeRFQId, setActiveRFQId] = useState(null);
  const [showStream, setShowStream] = useState(false);
  // New fields
  const [deliveryDate, setDeliveryDate] = useState("");
  const [location, setLocation] = useState("");
  const [locationType, setLocationType] = useState("saved"); // "saved" or "custom"
  const [savedLocations, setSavedLocations] = useState([]);
  const [notes, setNotes] = useState("");
  
  // Single-line address input and parsed result (kept for backend use)
  const [parsedLocation, setParsedLocation] = useState(null);

  // Google Places Autocomplete for the single-line location input
  const streetInputRef = useGooglePlacesAutocomplete(
    { types: ['address'] },
    (parsed) => {
      // Fill the visible single-line location and keep parsed details hidden
      const composed = [parsed.formattedAddress || [parsed.street, parsed.city, parsed.state, parsed.zipCode].filter(Boolean).join(', ')].filter(Boolean)[0];
      setLocation(composed || "");
      setParsedLocation(parsed);
      // Update the actual DOM input to match (since Google Places may have changed it)
      if (streetInputRef.current) {
        streetInputRef.current.value = composed || "";
      }
    }
  );
  
  // Material radius state - initialize from settings to prevent flicker
  const [searchRadius, setSearchRadius] = useState(() => {
    if (materialCategory && getMaterialRadiusSetting) {
      const categoryKey = materialCategory.toUpperCase().replace(/\s+/g, '_');
      return getMaterialRadiusSetting(categoryKey);
    }
    return 50;
  });
  const [radiusChanged, setRadiusChanged] = useState(false);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  
  // Duplicate RFQ handling
  const [duplicateRFQs, setDuplicateRFQs] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [nonDuplicateDealers, setNonDuplicateDealers] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [usingLocationFiltering, setUsingLocationFiltering] = useState(false);

  // Groups state
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Load saved delivery locations from backend and localStorage
  useEffect(() => {
    const loadDeliveryLocations = async () => {
      // First try localStorage
      try {
        const localSettings = localStorage.getItem("userSettings");
        if (localSettings) {
          const parsed = JSON.parse(localSettings);
          if (parsed.deliveryLocations && Array.isArray(parsed.deliveryLocations)) {
            setSavedLocations(parsed.deliveryLocations);
          }
        }
      } catch (err) {
        console.error("Failed to load from localStorage:", err);
      }

      // Then try backend using the correct endpoint
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch(getApiUrl(`/api/users/settings/${userId}`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Check if delivery locations are part of user settings
          if (data.deliveryLocations && Array.isArray(data.deliveryLocations)) {
            setSavedLocations(data.deliveryLocations);
          }
        }
      } catch (err) {
        console.error("Failed to load delivery locations from backend:", err);
        // Continue with localStorage data if backend fails
      }
    };

    loadDeliveryLocations();
  }, []);

  // Load groups
  useEffect(() => {
    const loadGroups = async () => {
      if (!clientId) return;
      
      try {
        setLoadingGroups(true);
        const data = await groupService.getAllGroups(clientId);
        setGroups(data);
      } catch (err) {
        console.error('[RFQScreen] Failed to load groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    loadGroups();
  }, [clientId]);

  // Load radius setting for current material category
  useEffect(() => {
    if (materialCategory && getMaterialRadiusSetting) {
      // Convert material category to uppercase and replace spaces with underscores
      const categoryKey = materialCategory.toUpperCase().replace(/\s+/g, '_');
      const radius = getMaterialRadiusSetting(categoryKey);
      setSearchRadius(radius);
      setRadiusChanged(false);
    }
  }, [materialCategory, getMaterialRadiusSetting]);

  // Fetch dealers based on radius and material category
  useEffect(() => {
    const fetchDealersByRadius = async () => {
      // Require a clientId and a materialSubcategory for location-based filtering
      if (!clientId || !materialSubcategory) {
        // If no clientId or subcategory, use the dealers passed as props
        setFilteredDealers(dealers);
        setInitialLoadComplete(true);
        setUsingLocationFiltering(false);
        return;
      }

      // We're using location-based filtering (based on subcategory)
      setUsingLocationFiltering(true);

      // Only show loading state if this is the first load
      if (!initialLoadComplete) {
        setLoadingDealers(true);
      }

      try {
        // Prefer materialSubcategory; fall back to materialCategory if needed
        const categorySource = materialSubcategory || materialCategory;
        // Convert to uppercase and replace spaces with underscores
        const categoryKey = String(categorySource).toUpperCase().replace(/\s+/g, '_');
        // Dealer location request for subcategory

        // Call the location-based dealer search API using the subcategory-based key
        const result = await findDealersNearby(clientId, categoryKey);
        
        // Filter companies by radius (API returns all companies with distance)
        const companiesWithinRadius = result.dealers.filter(
          company => company.distance <= searchRadius
        );
        
  // Found companiesWithinRadius
        
        // Transform to match the expected dealer structure
        // Each company becomes a dealer entry, but we'll store all individual dealers for RFQ submission
        const transformedDealers = companiesWithinRadius.map(company => ({
          id: company.companyId,
          name: company.companyName,
          location: `${company.latitude}, ${company.longitude}`,
          distance: company.distance,
          latitude: company.latitude,
          longitude: company.longitude,
          // Store individual dealers for later use when submitting RFQ
          individualDealers: company.dealers || [],
        }));
        
        setFilteredDealers(transformedDealers);
      } catch (error) {
        console.error('[RFQScreen] Error fetching dealers by radius:', error);
        // On error, set to empty array - don't fall back to dealers prop
        setFilteredDealers([]);
      } finally {
        if (!initialLoadComplete) {
          setLoadingDealers(false);
        }
        setInitialLoadComplete(true);
      }
    };

    // Debounce the dealer fetch to avoid too many API calls while sliding
    const timeoutId = setTimeout(() => {
      fetchDealersByRadius();
    }, 500); // Wait 500ms after slider stops moving

    return () => clearTimeout(timeoutId);
  }, [searchRadius, materialCategory, clientId, dealers, initialLoadComplete, materialSubcategory]);

  // Initialize and sync selectedDealers with dealers - use layoutEffect for synchronous updates
  useLayoutEffect(() => {
    setSelectedDealers(prev => {
      // Use filteredDealers if using location filtering, otherwise use dealers prop
      const dealersToUse = usingLocationFiltering ? filteredDealers : dealers;
      // Create new state based on current dealers
      const newState = {};
      dealersToUse.forEach(dealer => {
        // Preserve existing selection if available, otherwise default to true
        newState[dealer.id] = prev[dealer.id] !== undefined ? prev[dealer.id] : true;
      });
      return newState;
    });
  }, [dealers, filteredDealers, usingLocationFiltering]);

  const toggleDealer = (dealerId) => {
    setSelectedDealers(prev => ({
      ...prev,
      [dealerId]: !prev[dealerId]
    }));
  };

  const toggleAllDealers = () => {
    const dealersToUse = usingLocationFiltering ? filteredDealers : dealers;
    const allSelected = dealersToUse.every(dealer => selectedDealers[dealer.id]);
    const newState = dealersToUse.reduce(
      (acc, dealer) => ({ ...acc, [dealer.id]: !allSelected }), 
      {}
    );
    setSelectedDealers(newState);
  };

  const allDealersSelected = (() => {
    const dealersToUse = usingLocationFiltering ? filteredDealers : dealers;
    return dealersToUse.length > 0 && dealersToUse.every(dealer => selectedDealers[dealer.id] === true);
  })();

  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value, 10);
    setSearchRadius(newRadius);
    setRadiusChanged(true);
  };

  const saveRadiusSetting = async () => {
    if (materialCategory && updateMaterialRadiusSetting) {
      const categoryKey = materialCategory.toUpperCase().replace(/\s+/g, '_');
      await updateMaterialRadiusSetting(categoryKey, searchRadius);
      setRadiusChanged(false);
    }
  };

  // Group management functions
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (!clientId) {
      alert('User not authenticated');
      return;
    }

    try {
      const newGroup = await groupService.createGroup(clientId, newGroupName.trim());
      setGroups([...groups, newGroup]);
      setSelectedGroupId(newGroup.id.toString());
      setNewGroupName('');
      setShowCreateGroup(false);
    } catch (err) {
      console.error('[RFQScreen] Failed to create group:', err);
      alert(`Failed to create group: ${err.message}`);
    }
  };

  const addRFQToGroup = async (rfqNumber) => {
    if (!selectedGroupId || !rfqNumber || !clientId) return;

    try {
      await groupService.addRFQToGroup(clientId, parseInt(selectedGroupId), rfqNumber);
    } catch (err) {
      console.error('[RFQScreen] Failed to add RFQ to group:', err);
    }
  };

  const handleSubmit = async () => {
    const dealersToUse = usingLocationFiltering ? filteredDealers : dealers;

    // Extract dealer IDs
    // - If using location filtering (company-based), send company IDs (dealer.id)
    // - Otherwise (legacy/default), send individual dealer IDs if present, else dealer.id
    let activeDealers = [];
    dealersToUse
      .filter(dealer => selectedDealers[dealer.id])
      .forEach(dealer => {
        if (usingLocationFiltering) {
          // For companies returned by location API, dealer.id is the companyId
          activeDealers.push(dealer.id);
        } else {
          // Legacy behavior: prefer individual dealer IDs when available
          if (dealer.individualDealers && dealer.individualDealers.length > 0) {
            const individualIds = dealer.individualDealers.map(d => d.id);
            activeDealers.push(...individualIds);
          } else {
            activeDealers.push(dealer.id);
          }
        }
      });

    if (!quantity || activeDealers.length === 0) {
      setError("Please enter a quantity and select at least one dealer");
      return;
    }

    // Validate delivery date is not in the past
    if (deliveryDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day
      const selectedDate = new Date(deliveryDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        setError("Delivery date cannot be in the past");
        return;
      }
    }

    // Build location string from address fields if using custom location
    let deliveryLocation = location;
    if (locationType === "custom") {
      // Get the actual current value from the input if it exists
      const currentInputValue = streetInputRef.current?.value;
      
      // Prioritize parsedLocation from autocomplete if available
      if (parsedLocation) {
        deliveryLocation = parsedLocation.formattedAddress || [parsedLocation.street, parsedLocation.city, parsedLocation.state, parsedLocation.zipCode].filter(Boolean).join(', ');
      } else if (currentInputValue) {
        // Use the current input value
        deliveryLocation = currentInputValue;
      } else if (!location) {
        // No autocomplete selection and no typed location
        deliveryLocation = null;
      }
      // Otherwise use the typed location value
    }

    const materialName = String(title || "").replace(/\s*RFQ$/i, "").trim();

    const payload = {
      clientId: clientId,
      dealerIds: activeDealers,
      material: materialName,
      quantity: parseFloat(quantity),
      unit,
      deliveryDate: deliveryDate || null,
      location: deliveryLocation || null,
      notes: notes.trim() || null
    };

  // RFQ payload ready to send

    // Log RFQ send action with endpoint and payload (temporary)
    try {
      const endpointUrl = getApiUrl(apiEndpoint);
      console.log(`[RFQScreen] Sending RFQ to ${endpointUrl} at ${new Date().toISOString()}`);
      console.log('[RFQScreen] RFQ payload:', payload);
    } catch (logErr) {
      // Continue if logging fails
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const res = await fetch(getApiUrl(apiEndpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();
      setResponse(data);
      
      // Check if response contains duplicates (rfqNumber === "-1" indicates duplicates)
      if (Array.isArray(data)) {
        // Check if any responses indicate duplicates
        const duplicates = data.filter(item => item.rfqNumber === "-1" && item.status && item.status.includes("Duplicate"));
        if (duplicates.length > 0) {
          // Extract duplicate dealer IDs from status messages
          const duplicateDealerIds = duplicates.map(dup => {
            const match = dup.status.match(/supplier\s+(\d+)/i);
            return match ? parseInt(match[1]) : null;
          }).filter(id => id !== null);
          
          // Separate non-duplicate dealers
          const nonDups = payload.dealerIds.filter(id => !duplicateDealerIds.includes(id));
          
          // Create a mapping of dealer IDs to dealer names for display
          const dealerMap = {};
          dealersToUse.forEach(dealer => {
            if (usingLocationFiltering) {
              dealerMap[dealer.id] = dealer.name || dealer.companyName || `Dealer ${dealer.id}`;
            } else {
              if (dealer.individualDealers && dealer.individualDealers.length > 0) {
                dealer.individualDealers.forEach(d => {
                  dealerMap[d.id] = d.name || dealer.name || dealer.companyName || `Dealer ${d.id}`;
                });
              } else {
                dealerMap[dealer.id] = dealer.name || dealer.companyName || `Dealer ${dealer.id}`;
              }
            }
          });
          
          // Attach dealer mapping to duplicates
          const duplicatesWithNames = duplicates.map(dup => ({
            ...dup,
            dealerMap
          }));
          
          // All duplicates will be overridden
          
          setDuplicateRFQs(duplicatesWithNames);
          setPendingPayload(payload);
          setNonDuplicateDealers(nonDups);
          setShowDuplicateModal(true);
          setLoading(false);
          return;
        }
        
        // No duplicates in array response - add successful RFQs to group
        if (selectedGroupId) {
          for (const item of data) {
            if (item && item.rfqNumber && item.rfqNumber !== "-1") {
              await addRFQToGroup(item.rfqNumber);
            }
          }
        }
        
        // Start streaming with first successful result
        const firstSuccess = data.find(item => item.rfqId);
        if (firstSuccess && firstSuccess.rfqId) {
          setActiveRFQId(firstSuccess.rfqId);
          setShowStream(true);
        }
        setLoading(false);
        return;
      }
      
      // Single object response
      if (data.rfqId) {
        setActiveRFQId(data.rfqId);
        setShowStream(true);
        
        // Add to group if one is selected
        if (selectedGroupId && data.rfqNumber) {
          await addRFQToGroup(data.rfqNumber);
        }
      }
    } catch (err) {
      // Log error
      try { console.error('[RFQScreen] Failed to send RFQ:', err); } catch (logErr) {}
      setError(err.message || "Failed to submit RFQ");
    } finally {
      setLoading(false);
    }
  };

  const closeStream = () => {
    setShowStream(false);
    setActiveRFQId(null);
  };

  // Removed unused handleOverrideRFQ function
  /*
  const handleOverrideRFQ = async () => {
    if (!pendingPayload) return;
    
    setLoading(true);
    setError(null);
    setShowDuplicateModal(false);
    
    try {
      // Process each duplicate RFQ separately
      const overridePromises = duplicateRFQs.map(async (duplicate) => {
        const match = duplicate.status.match(/supplier\s+(\d+).*?Number:\s*([a-f0-9-]+)/i);
        const supplierId = match ? match[1] : null;
        const existingRfqNumber = match ? match[2] : null;
        
        if (!existingRfqNumber || !supplierId) {
          console.warn("Could not extract RFQ number or supplier ID from:", duplicate.status);
          return null;
        }
        
        // Create payload with only the duplicate dealer ID
        const editPayload = {
          ...pendingPayload,
          dealerIds: [parseInt(supplierId)],
          overrideRFQ: true
        };
        
        const res = await fetch(getApiUrl(`/api/rfq/${existingRfqNumber}/edit`), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editPayload),
        });

        if (!res.ok) {
          throw new Error(`API error: ${res.status}`);
        }

        return res.json();
      });
      
      const overrideResults = await Promise.all(overridePromises);
      const successOverrides = overrideResults.filter(r => r !== null);
      
      console.log(`[RFQScreen] Override RFQ responses:`, successOverrides);
      
      // Now send RFQs to non-duplicate dealers if any
      if (nonDuplicateDealers.length > 0) {
        const newRfqPayload = {
          ...pendingPayload,
          dealerIds: nonDuplicateDealers
        };
        
        const newRes = await fetch(getApiUrl(apiEndpoint), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newRfqPayload),
        });

        if (!newRes.ok) {
          throw new Error(`API error: ${newRes.status}`);
        }

        const newData = await newRes.json();
        console.log(`[RFQScreen] New RFQ for non-duplicate dealers:`, newData);
        
        // If API returns an RFQ ID, start streaming
        if (newData.rfqId) {
          setActiveRFQId(newData.rfqId);
          setShowStream(true);
        }
      } else {
        // If only overrides, use first successful override for streaming
        const firstSuccess = successOverrides.find(r => r.rfqId);
        if (firstSuccess && firstSuccess.rfqId) {
          setActiveRFQId(firstSuccess.rfqId);
          setShowStream(true);
        }
      }
      
      setResponse(successOverrides.length === 1 ? successOverrides[0] : successOverrides);
      
      // Clear duplicate state
      setDuplicateRFQs([]);
      setPendingPayload(null);
      setNonDuplicateDealers([]);
    } catch (err) {
      console.error('[RFQScreen] Failed to override RFQ:', err);
      setError(err.message || "Failed to override RFQ");
    } finally {
      setLoading(false);
    }
  };
  */
  const handleSubmitDuplicates = async () => {
    if (!pendingPayload) return;
    
    setLoading(true);
    setError(null);
    setShowDuplicateModal(false);
    
    try {
      const allPromises = [];
      
      // Override all duplicate RFQs
      duplicateRFQs.forEach((duplicate) => {
        const match = duplicate.status.match(/supplier\s+(\d+).*?Number:\s*([a-f0-9-]+)/i);
        const supplierId = match ? match[1] : null;
        const existingRfqNumber = match ? match[2] : null;
        
        if (!existingRfqNumber || !supplierId) {
          console.warn("Could not extract RFQ number or supplier ID from:", duplicate.status);
          return;
        }
        
        const dealerId = parseInt(supplierId);
        
        // Override existing RFQ
        const editPayload = {
          ...pendingPayload,
          dealerIds: [dealerId],
          overrideRFQ: true
        };
        
        const promise = fetch(getApiUrl(`/api/rfq/${existingRfqNumber}/edit`), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editPayload),
        }).then(res => res.ok ? res.json() : null);
        
        allPromises.push(promise);
      });
      
      // Send RFQs to non-duplicate dealers if any
      if (nonDuplicateDealers.length > 0) {
        const newRfqPayload = {
          ...pendingPayload,
          dealerIds: nonDuplicateDealers
        };
        
        const newPromise = fetch(getApiUrl(apiEndpoint), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newRfqPayload),
        }).then(res => res.ok ? res.json() : null);
        
        allPromises.push(newPromise);
      }
      
      const results = await Promise.all(allPromises);
      const successResults = results.filter(r => r !== null);
      
      console.log(`[RFQScreen] Duplicate handling results:`, successResults);
      
      // Start streaming with first successful result
      const firstSuccess = successResults.find(r => r.rfqId);
      if (firstSuccess && firstSuccess.rfqId) {
        setActiveRFQId(firstSuccess.rfqId);
        setShowStream(true);
      }
      
      setResponse(successResults.length === 1 ? successResults[0] : successResults);
      
      // Add all successful RFQs to the selected group
      if (selectedGroupId) {
        for (const result of successResults) {
          if (result && result.rfqNumber) {
            await addRFQToGroup(result.rfqNumber);
          }
        }
      }
      
      // Clear duplicate state
      setDuplicateRFQs([]);
      setPendingPayload(null);
      setNonDuplicateDealers([]);
    } catch (err) {
      console.error('[RFQScreen] Failed to handle duplicates:', err);
      setError(err.message || "Failed to handle duplicate RFQs");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOverride = () => {
    setShowDuplicateModal(false);
    setDuplicateRFQs([]);
    setPendingPayload(null);
    setNonDuplicateDealers([]);
    setLoading(false);
  };

  // If streaming is active, show the stream component
  if (showStream && activeRFQId) {
    return <RFQStream rfqId={activeRFQId} onClose={closeStream} />;
  }

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>

      {/* Quantity Input */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-2">
          Quantity ({unit})
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            placeholder={placeholder}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            style={{ MozAppearance: 'textfield' }}
          />
          <span className="text-gray-600 dark:text-gray-400 font-medium">{unit}</span>
        </div>
      </div>

      {/* Search Radius Slider */}
      {materialCategory && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Dealer Search Radius
            </label>
            {radiusChanged && (
              <button
                onClick={saveRadiusSetting}
                className="text-xs px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
              >
                Save Default
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="10"
              max="200"
              step="5"
              value={searchRadius}
              onChange={handleRadiusChange}
              className="flex-1 h-2 bg-blue-200 dark:bg-blue-800 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex items-center gap-2 min-w-[100px]">
              <input
                type="number"
                min="10"
                max="200"
                value={searchRadius}
                onChange={handleRadiusChange}
                className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">miles</span>
            </div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            {radiusChanged ? (
              <span className="text-blue-700 font-medium">
                Click "Save Default" to remember this radius for {materialCategory}
              </span>
            ) : (
              <span>
                {loadingDealers ? (
                  "Searching for dealers..."
                ) : filteredDealers.length > 0 ? (
                  `Found ${filteredDealers.length} dealer${filteredDealers.length !== 1 ? 's' : ''} within ${searchRadius} miles`
                ) : (
                  `No dealers found within ${searchRadius} miles`
                )}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Dealer Selection */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-semibold">
            Select {filteredDealers.length > 0 ? 'Companies' : 'Dealers'} {loadingDealers && <span className="text-xs text-gray-500">(Loading...)</span>}
          </label>
          <button
            onClick={toggleAllDealers}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {allDealersSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded p-3 bg-gray-50 dark:bg-gray-700">
          {(() => {
            // While loading or before initial load completes, show loading state
            if (loadingDealers || !initialLoadComplete) {
              return <p className="text-sm text-gray-500 dark:text-gray-400">Loading {materialCategory ? 'companies' : 'dealers'}...</p>;
            }
            
            // If using location filtering, only use filteredDealers; otherwise use dealers prop
            const dealersToUse = usingLocationFiltering ? filteredDealers : dealers;
            
            // Check if selectedDealers state is in sync with current dealers
            const dealersInitialized = dealersToUse.length > 0 && 
              dealersToUse.every(dealer => selectedDealers[dealer.id] !== undefined);
            
            if (!dealersInitialized && dealersToUse.length > 0) {
              return <p className="text-sm text-gray-500 dark:text-gray-400">Loading {materialCategory ? 'companies' : 'dealers'}...</p>;
            }
            
            if (dealersToUse.length === 0) {
              return <p className="text-sm text-gray-500 dark:text-gray-400">No {materialCategory ? 'companies' : 'dealers'} available within {searchRadius} miles</p>;
            }
            
            return dealersToUse.map((dealer) => (
              <label
                key={dealer.id}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedDealers[dealer.id] === true}
                  onChange={() => toggleDealer(dealer.id)}
                  className="w-4 h-4 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{dealer.name}</span>
                    {dealer.individualDealers && dealer.individualDealers.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded">
                        {dealer.individualDealers.length} rep{dealer.individualDealers.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {settings.generalSettings.showDealerLocations && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {dealer.distance !== undefined && `${dealer.distance.toFixed(1)} miles away`}
                    </div>
                  )}
                </div>
              </label>
            ));
          })()}
        </div>
      </div>

      {/* Delivery Details */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Delivery Date</label>
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Delivery Location</label>
          
          {/* Location Type Toggle */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                setLocationType("saved");
                setLocation("");
              }}
              className={`flex-1 px-4 py-2 rounded transition-colors ${
                locationType === "saved"
                  ? "bg-blue-600 dark:bg-blue-700 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              Saved Location
            </button>
            <button
              type="button"
              onClick={() => {
                setLocationType("custom");
                setLocation("");
              }}
              className={`flex-1 px-4 py-2 rounded transition-colors ${
                locationType === "custom"
                  ? "bg-blue-600 dark:bg-blue-700 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              Custom Location
            </button>
          </div>

          {/* Saved Location Selector */}
          {locationType === "saved" && (
            <div>
              {savedLocations.length === 0 ? (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-800 dark:text-yellow-200">
                  No saved locations. Add locations in Settings or use Custom Location.
                </div>
              ) : (
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select a saved location...</option>
                  {Array.isArray(savedLocations) && savedLocations.map((loc) => (
                    <option key={loc.id} value={loc.address}>
                      {loc.name} - {loc.address}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Custom Location Input */}
          {locationType === "custom" && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Address</label>
                <input
                  ref={streetInputRef}
                  type="text"
                  defaultValue={location}
                  key={locationType}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    // Clear parsed details if user types manually
                    setParsedLocation(null);
                  }}
                  placeholder="Enter address..."
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes (replaces Specifications) */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Notes / Special Requirements
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter any special requirements, specifications, delivery instructions, or other notes..."
          rows={5}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Include details like grade, coating, dimensions, delivery instructions, or any other requirements.
        </p>
      </div>

      {/* Group Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Add to Group (Optional)
        </label>
        <div className="flex gap-2">
          <select
            value={selectedGroupId}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'create_new') {
                setShowCreateGroup(true);
                setSelectedGroupId('');
              } else {
                setSelectedGroupId(value);
                setShowCreateGroup(false);
              }
            }}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            disabled={loadingGroups}
          >
            <option value="">No Group</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.rfqs?.length || 0} RFQs)
              </option>
            ))}
            <option value="create_new">+ Create New Group</option>
          </select>
        </div>

        {showCreateGroup && (
          <div className="mt-3 p-3 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800">
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
              New Group Name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateGroup();
                  }
                }}
                placeholder="Enter group name..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                autoFocus
              />
              <button
                onClick={handleCreateGroup}
                className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateGroup(false);
                  setNewGroupName('');
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Organize this RFQ by adding it to a group. Groups can be managed from the Groups tab.
        </p>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="px-6 py-3 bg-blue-600 dark:bg-blue-700 text-white font-semibold rounded hover:bg-blue-700 dark:hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? "Submitting..." : "Request Quote"}
      </button>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded text-red-700 dark:text-red-200">
          <p className="font-semibold">Error:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Success Response */}
      {response && (
        <div className="mt-4 p-4 bg-green-50 border border-green-300 rounded text-green-700">
          <p className="font-semibold">Success!</p>
          <pre className="text-sm mt-2 whitespace-pre-wrap">
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      )}

      {/* Duplicate RFQ Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <svg className="w-8 h-8 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Duplicate RFQ Detected
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You have already sent an RFQ for this material to the following supplier(s):
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6 space-y-4">
              {duplicateRFQs.map((dup, index) => {
                const match = dup.status.match(/supplier\s+(\d+).*?RFQ ID:\s*(\d+).*?Number:\s*([a-f0-9-]+)/i);
                const supplierId = match ? match[1] : 'Unknown';
                // const rfqId = match ? match[2] : 'Unknown'; // Unused variable
                const rfqNumber = match ? match[3] : 'Unknown';
                
                // Get supplier name from dealer mapping
                const supplierName = dup.dealerMap && dup.dealerMap[supplierId] 
                  ? dup.dealerMap[supplierId] 
                  : `Supplier ${supplierId}`;
                
                return (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">Supplier:</span> {supplierName}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">Existing RFQ:</span> #{rfqNumber}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">Material:</span> {dup.material}
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <span className="px-4 py-2 rounded-lg font-medium text-sm bg-yellow-600 dark:bg-yellow-700 text-white">
                          🔄 Will Override
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                🔄 <strong>Override Action:</strong> All existing RFQs will be updated with your new details. Click "Override All" to proceed or "Cancel" to abort.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelOverride}
                className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDuplicates}
                disabled={loading}
                className="px-6 py-2 bg-yellow-600 dark:bg-yellow-700 text-white font-medium rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Override All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
